import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import RefundsTable from '../components/RefundsTable';
import { fetchWithAuth } from '../util/fetchWithAuth';
import { Refund, Order } from '../types';
import AddRefundModal from '../modals/AddRefundModal';

const Refunds: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [refunds, setRefunds] = useState<Refund[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRefund, setSelectedRefund] = useState<Refund | null>(null);
  const [orders, setOrders] = useState<Order[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const fetchRefunds = async (search: string = '', page: number = 1) => {
    setIsLoading(true);
    try {
      const url = search
        ? `/refunds?search=${encodeURIComponent(search)}&page=${page}`
        : `/refunds?page=${page}`;

      const res = await fetchWithAuth(url);
      const data = await res.json();
      setRefunds(data.refunds ?? []);
      setCurrentPage(data.current_page);
      setTotalPages(data.total_pages);
    } catch (err) {
      console.error('Error fetching refunds:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const initialPage = parseInt(searchParams.get('page') || '1', 10);
    const page = isNaN(initialPage) || initialPage < 1 ? 1 : initialPage;
    fetchRefunds('', page);
    fetchWithAuth('/orders')
      .then(res => res.json())
      .then(data => setOrders(data.orders ?? []))
      .catch(err => console.error('Error fetching orders:', err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleEditRefund = (refund: Refund) => {
    setSelectedRefund(refund);
    setIsModalOpen(true);
  };

  return (
    <div className="px-4 md:px-6 py-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
        <h1 className="text-2xl font-bold text-gray-800">Refunds</h1>
        <button
          onClick={() => {
            setSelectedRefund(null);
            setIsModalOpen(true);
          }}
          className="w-full sm:w-auto bg-black text-white px-4 py-2 rounded-md font-semibold hover:bg-gray-800"
        >
          Add Refund
        </button>
      </div>

      <div className="flex flex-col md:flex-row items-stretch md:items-center gap-2 mb-4">
        <input
          type="text"
          placeholder="Search by Order Reference, Customer name"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              setSearchParams(prev => { prev.set('page', '1'); return prev; });
              fetchRefunds(searchTerm, 1);
            }
          }}
          className="border px-4 py-2 rounded-md w-full md:w-96"
        />
        <button
          onClick={() => {
            setSearchParams(prev => { prev.set('page', '1'); return prev; });
            fetchRefunds(searchTerm, 1);
          }}
          className="w-full sm:w-auto bg-black text-white px-4 py-2 rounded-md font-semibold hover:bg-gray-800"
        >
          Search
        </button>
        {searchTerm && (
          <button
            onClick={() => {
              setSearchTerm('');
              setSearchParams(prev => { prev.set('page', '1'); return prev; });
              fetchRefunds('', 1);
            }}
            className="w-full sm:w-auto bg-gray-300 text-gray-800 px-4 py-2 rounded-md font-semibold hover:bg-gray-400"
          >
            Clear
          </button>
        )}
      </div>

      {isLoading && (
        <div className="w-full h-1 bg-gray-200 rounded overflow-hidden mb-2">
          <div className="h-full bg-blue-500 animate-pulse" style={{ width: '100%' }}></div>
        </div>
      )}
      
      <RefundsTable
        refunds={refunds}
        onEdit={handleEditRefund}
        onSuccess={() => {
          fetchRefunds(searchTerm, currentPage);
        }}
      />

      <div className="flex justify-center items-center space-x-4 mt-4">
        <button
          disabled={currentPage === 1}
          onClick={() => {
            const newPage = currentPage - 1;
            setSearchParams(prev => { prev.set('page', String(newPage)); return prev; });
            fetchRefunds(searchTerm, newPage);
          }}
          className="px-4 py-2 bg-gray-200 rounded disabled:opacity-50"
        >
          Previous
        </button>
        <span>Page {currentPage} of {totalPages}</span>
        <button
          disabled={currentPage === totalPages}
          onClick={() => {
            const newPage = currentPage + 1;
            setSearchParams(prev => { prev.set('page', String(newPage)); return prev; });
            fetchRefunds(searchTerm, newPage);
          }}
          className="px-4 py-2 bg-gray-200 rounded disabled:opacity-50"
        >
          Next
        </button>
      </div>

      <AddRefundModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedRefund(null);
        }}
        onSuccess={() => {
          setIsModalOpen(false);
          setSelectedRefund(null);
          fetchRefunds(searchTerm, currentPage);
        }}
        defaultValue={selectedRefund}
        orders={orders}
      />
    </div>
  );
};

export default Refunds;
